/**
 * Profile Page
 *
 * User profile management with refined design
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { ErrorReason } from '@polygraf/shared'
import {
	AlertTriangle,
	Check,
	Eye,
	EyeOff,
	Image,
	Lock,
	Mail,
	Shield,
	Trash2,
	User,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { Alert, Button, Modal, Spinner, TextField } from '../components/ui'
import { usePageTitle } from '../hooks/use-page-title'
import { changePassword, signOut, useSession } from '../lib/auth-client'
import { trpc } from '../trpc/client'

// Schema creation functions
const createProfileSchema = (t: (key: string) => string) =>
	z.object({
		name: z
			.string()
			.min(2, t('profile.validation.nameMin'))
			.max(100, t('profile.validation.nameMax')),
		// Mirror the server's avatarUrlSchema (users.ts): https only, no query
		// string or fragment, max 2048 chars. `.url()` accepted http:// the
		// server rejects, so the client passed validation then failed on submit.
		image: z
			.string()
			.refine((url) => {
				if (url === '') return true
				try {
					const parsed = new URL(url)
					return (
						parsed.protocol === 'https:' &&
						parsed.search === '' &&
						parsed.hash === '' &&
						url.length <= 2048
					)
				} catch {
					return false
				}
			}, t('profile.validation.invalidUrl'))
			.optional()
			.or(z.literal('')),
	})

const createPasswordSchema = (t: (key: string) => string) =>
	z
		.object({
			currentPassword: z.string().min(1, t('profile.validation.currentPasswordRequired')),
			newPassword: z.string().min(8, t('profile.validation.passwordMin')),
			confirmPassword: z.string(),
		})
		.refine((data) => data.newPassword === data.confirmPassword, {
			message: t('profile.validation.passwordMismatch'),
			path: ['confirmPassword'],
		})

type ProfileFormData = z.infer<ReturnType<typeof createProfileSchema>>
type PasswordFormData = z.infer<ReturnType<typeof createPasswordSchema>>

export default function ProfilePage() {
	const { t } = useTranslation()
	usePageTitle(t('profile.title'))
	const navigate = useNavigate()
	const { data: session, isPending: isSessionLoading } = useSession()

	// Redirect if not logged in
	useEffect(() => {
		if (!isSessionLoading && !session?.user) {
			// Keep the destination through sign-in. The mobile app opens
			// `/profile?action=delete-account` in the browser, where the user is
			// usually signed out — without `returnTo` they would land on the
			// home page after signing in and have to find the delete button
			// themselves (App Store guideline 5.1.1(v) wants the link to lead
			// straight to deletion).
			const here = `${window.location.pathname}${window.location.search}`
			navigate(`/auth?returnTo=${encodeURIComponent(here)}`)
		}
	}, [session, isSessionLoading, navigate])

	// Show loading state while checking session
	if (isSessionLoading) {
		return (
			<div className="max-w-2xl mx-auto space-y-6 animate-enter">
				<div className="animate-pulse space-y-4">
					<div className="h-8 w-1/3 bg-base-300 rounded" />
					<div className="h-4 w-1/2 bg-base-300 rounded" />
				</div>
			</div>
		)
	}

	// Don't render if not logged in
	if (!session?.user) {
		return null
	}

	return (
		<div className="max-w-2xl mx-auto space-y-6 animate-enter">
			<div className="mb-8">
				<h1>{t('profile.title')}</h1>
				<p className="text-base-content/60 mt-2">{t('profile.subtitle')}</p>
			</div>

			<ProfileInfoSection />
			<PasswordSection />
			<DangerZoneSection />
		</div>
	)
}

function ProfileInfoSection() {
	const { t } = useTranslation()
	const utils = trpc.useUtils()
	const { data: user, isLoading } = trpc.users.me.useQuery()
	const [isEditing, setIsEditing] = useState(false)
	const [success, setSuccess] = useState<string | null>(null)
	const [error, setError] = useState<string | null>(null)

	const profileSchema = createProfileSchema(t)
	const form = useForm<ProfileFormData>({
		resolver: zodResolver(profileSchema),
		values: {
			name: user?.name ?? '',
			image: user?.image ?? '',
		},
	})

	const updateMutation = trpc.users.updateProfile.useMutation({
		onSuccess: () => {
			utils.users.me.invalidate()
			setSuccess(t('profile.updated'))
			setError(null)
			setIsEditing(false)
			setTimeout(() => setSuccess(null), 3000)
		},
		onError: (err) => {
			setError(err.message ?? t('profile.errors.updateFailed'))
			setSuccess(null)
		},
	})

	const handleSubmit = (data: ProfileFormData) => {
		updateMutation.mutate({
			name: data.name || undefined,
			image: data.image || undefined,
		})
	}

	if (isLoading) {
		return (
			<div className="settings-card">
				<div className="animate-pulse space-y-4">
					<div className="h-6 w-1/3 bg-base-300 rounded" />
					<div className="h-10 bg-base-300 rounded" />
					<div className="h-10 bg-base-300 rounded" />
				</div>
			</div>
		)
	}

	return (
		<div className="settings-card">
			<h2>
				<User size={20} className="text-primary" />
				<span>{t('profile.personalInfo')}</span>
			</h2>

			{success && (
				<Alert tone="success" className="mb-4">
					{success}
				</Alert>
			)}

			{error && (
				<Alert tone="error" className="mb-4">
					{error}
				</Alert>
			)}

			<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
				{/* Email (read-only) */}
				<TextField
					id="profile-email"
					type="email"
					label={t('profile.email')}
					value={user?.email ?? ''}
					disabled
					leadingIcon={<Mail size={18} />}
					hint={t('profile.emailDesc')}
					className="bg-base-200 cursor-not-allowed"
				/>

				{/* Name */}
				<TextField
					id="profile-name"
					type="text"
					label={t('profile.name')}
					placeholder={t('profile.namePlaceholder')}
					disabled={!isEditing}
					required
					leadingIcon={<User size={18} />}
					className={!isEditing ? 'bg-base-200' : undefined}
					error={form.formState.errors.name?.message}
					{...form.register('name')}
				/>

				{/* Avatar URL */}
				<TextField
					id="profile-image"
					type="url"
					label={t('profile.avatar')}
					placeholder={t('profile.avatarPlaceholder')}
					disabled={!isEditing}
					leadingIcon={<Image size={18} />}
					className={!isEditing ? 'bg-base-200' : undefined}
					error={form.formState.errors.image?.message}
					hint={t('profile.avatarDesc')}
					{...form.register('image')}
				/>

				{/* Action Buttons */}
				<div className="flex gap-2 pt-2">
					{isEditing ? (
						<>
							<Button type="submit" disabled={updateMutation.isPending}>
								{updateMutation.isPending ? (
									<>
										<Spinner size="sm" />
										{t('profile.updating')}
									</>
								) : (
									<>
										<Check size={16} />
										{t('profile.updateProfile')}
									</>
								)}
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => {
									setIsEditing(false)
									form.reset()
									setError(null)
								}}
							>
								{t('common.cancel')}
							</Button>
						</>
					) : (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setIsEditing(true)}
							className="border border-base-300"
						>
							{t('common.edit')}
						</Button>
					)}
				</div>
			</form>
		</div>
	)
}

function PasswordSection() {
	const { t } = useTranslation()
	const [showCurrentPassword, setShowCurrentPassword] = useState(false)
	const [showNewPassword, setShowNewPassword] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [success, setSuccess] = useState<string | null>(null)
	const [error, setError] = useState<string | null>(null)

	const passwordSchema = createPasswordSchema(t)
	const form = useForm<PasswordFormData>({
		resolver: zodResolver(passwordSchema),
	})

	const handleSubmit = async (data: PasswordFormData) => {
		setIsLoading(true)
		setError(null)
		setSuccess(null)

		try {
			const result = await changePassword({
				currentPassword: data.currentPassword,
				newPassword: data.newPassword,
			})

			if (result.error) {
				setError(result.error.message ?? t('profile.errors.passwordChangeFailed'))
				return
			}

			setSuccess(t('profile.passwordChanged'))
			form.reset()
			setTimeout(() => setSuccess(null), 3000)
		} catch {
			setError(t('profile.errors.passwordChangeFailed'))
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className="settings-card">
			<h2>
				<Shield size={20} className="text-sage-500" />
				<span>{t('profile.security')}</span>
			</h2>

			{success && (
				<Alert tone="success" className="mb-4">
					{success}
				</Alert>
			)}

			{error && (
				<Alert tone="error" className="mb-4">
					{error}
				</Alert>
			)}

			<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
				<p className="text-sm text-base-content/70 mb-4">{t('profile.changePassword')}</p>

				{/* Current Password */}
				<div>
					<label htmlFor="current-password" className="label">
						<span className="label-text">{t('profile.currentPassword')}</span>
					</label>
					<div className="relative">
						<Lock
							size={18}
							className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40"
						/>
						<input
							id="current-password"
							type={showCurrentPassword ? 'text' : 'password'}
							placeholder="••••••••"
							className="input-refined w-full pl-10 pr-10"
							autoComplete="current-password"
							required
							aria-required="true"
							{...form.register('currentPassword')}
							aria-invalid={!!form.formState.errors.currentPassword}
							aria-describedby={
								form.formState.errors.currentPassword ? 'current-password-error' : undefined
							}
						/>
						<button
							type="button"
							onClick={() => setShowCurrentPassword(!showCurrentPassword)}
							className="absolute right-0 top-1/2 -translate-y-1/2 p-3 text-base-content/40 hover:text-base-content"
							aria-label={showCurrentPassword ? t('auth.hidePassword') : t('auth.showPassword')}
						>
							{showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
						</button>
					</div>
					{form.formState.errors.currentPassword && (
						<p id="current-password-error" role="alert" className="text-error text-sm mt-1">
							{form.formState.errors.currentPassword.message}
						</p>
					)}
				</div>

				{/* New Password */}
				<div>
					<label htmlFor="new-password" className="label">
						<span className="label-text">{t('profile.newPassword')}</span>
					</label>
					<div className="relative">
						<Lock
							size={18}
							className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40"
						/>
						<input
							id="new-password"
							type={showNewPassword ? 'text' : 'password'}
							placeholder="••••••••"
							className="input-refined w-full pl-10 pr-10"
							autoComplete="new-password"
							required
							aria-required="true"
							{...form.register('newPassword')}
							aria-invalid={!!form.formState.errors.newPassword}
							aria-describedby={
								form.formState.errors.newPassword ? 'new-password-error' : undefined
							}
						/>
						<button
							type="button"
							onClick={() => setShowNewPassword(!showNewPassword)}
							className="absolute right-0 top-1/2 -translate-y-1/2 p-3 text-base-content/40 hover:text-base-content"
							aria-label={showNewPassword ? t('auth.hidePassword') : t('auth.showPassword')}
						>
							{showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
						</button>
					</div>
					{form.formState.errors.newPassword && (
						<p id="new-password-error" role="alert" className="text-error text-sm mt-1">
							{form.formState.errors.newPassword.message}
						</p>
					)}
				</div>

				{/* Confirm New Password */}
				<div>
					<label htmlFor="confirm-new-password" className="label">
						<span className="label-text">{t('profile.confirmNewPassword')}</span>
					</label>
					<div className="relative">
						<Lock
							size={18}
							className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40"
						/>
						<input
							id="confirm-new-password"
							type={showNewPassword ? 'text' : 'password'}
							placeholder="••••••••"
							className="input-refined w-full pl-10"
							autoComplete="new-password"
							required
							aria-required="true"
							{...form.register('confirmPassword')}
							aria-invalid={!!form.formState.errors.confirmPassword}
							aria-describedby={
								form.formState.errors.confirmPassword ? 'confirm-new-password-error' : undefined
							}
						/>
					</div>
					{form.formState.errors.confirmPassword && (
						<p id="confirm-new-password-error" role="alert" className="text-error text-sm mt-1">
							{form.formState.errors.confirmPassword.message}
						</p>
					)}
				</div>

				<Button type="submit" disabled={isLoading}>
					{isLoading ? (
						<>
							<Spinner size="sm" />
							{t('profile.changingPassword')}
						</>
					) : (
						t('profile.changePasswordButton')
					)}
				</Button>
			</form>
		</div>
	)
}

/** Query flag that opens the delete dialog directly (used by the mobile app). */
const DELETE_ACCOUNT_ACTION = 'delete-account'

function DangerZoneSection() {
	const { t } = useTranslation()
	const { data: session } = useSession()
	const [searchParams, setSearchParams] = useSearchParams()
	const [showModal, setShowModal] = useState(false)
	const [password, setPassword] = useState('')
	const [confirmEmail, setConfirmEmail] = useState('')
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// `/profile?action=delete-account` opens the dialog straight away. The flag
	// is consumed so a reload, or closing the dialog, does not reopen it.
	useEffect(() => {
		if (searchParams.get('action') !== DELETE_ACCOUNT_ACTION) return
		setShowModal(true)
		const next = new URLSearchParams(searchParams)
		next.delete('action')
		setSearchParams(next, { replace: true })
	}, [searchParams, setSearchParams])

	// Which confirmation to ask for, and what the cascade takes with the
	// account. Fetched when the dialog opens, not on every profile visit.
	// Always refetched on open: a space or session created in another tab since
	// the last look must still appear in the warning. One retry, so a real
	// failure surfaces in seconds rather than after the default three.
	const preview = trpc.users.deletionPreview.useQuery(undefined, {
		enabled: showModal,
		staleTime: 0,
		refetchOnMount: 'always',
		retry: 1,
	})
	const hasPassword = preview.data?.hasPassword ?? false
	const ownedSpaces = preview.data?.ownedSpaces ?? []
	const activeLiveSessions = preview.data?.activeLiveSessions ?? []
	const uploadedFileCount = preview.data?.uploadedFileCount ?? 0
	const accountEmail = session?.user.email ?? ''

	const confirmed = hasPassword
		? password.length > 0
		: confirmEmail.trim().toLowerCase() === accountEmail.trim().toLowerCase() &&
			accountEmail.length > 0

	const closeModal = useCallback(() => {
		if (isLoading) return
		setShowModal(false)
		setPassword('')
		setConfirmEmail('')
		setError(null)
	}, [isLoading])

	const deleteMutation = trpc.users.deleteAccount.useMutation({
		onSuccess: async () => {
			try {
				await signOut()
			} catch (err) {
				console.error('Sign out failed after account deletion:', err)
			} finally {
				// Force navigation regardless of sign out result
				window.location.href = '/'
			}
		},
		onError: (err) => {
			if (err.data?.code === 'TOO_MANY_REQUESTS' || err.message.startsWith('Too many')) {
				setError(t('profile.errors.tooManyDeleteAttempts'))
			} else if (err.message === 'Incorrect password') {
				setError(t('profile.errors.wrongDeletePassword'))
			} else if (err.data?.reason === ErrorReason.CONFIRM_EMAIL_MISMATCH) {
				setError(t('profile.errors.deleteEmailMismatch'))
			} else {
				setError(err.message ?? t('profile.errors.deleteFailed'))
			}
			setIsLoading(false)
		},
	})

	const handleDelete = () => {
		if (!confirmed || !preview.data) return
		setIsLoading(true)
		setError(null)
		deleteMutation.mutate(hasPassword ? { password } : { confirmEmail })
	}

	return (
		<>
			<div className="settings-card border-error/30" id="delete-account">
				<h2>
					<AlertTriangle size={20} className="text-coral-700" />
					<span className="text-coral-700">{t('profile.dangerZone')}</span>
				</h2>

				<div className="space-y-4">
					<div>
						<p className="font-medium">{t('profile.deleteAccount')}</p>
						<p className="text-sm text-base-content/60">{t('profile.deleteAccountDesc')}</p>
					</div>

					<Button
						variant="ghost"
						size="sm"
						onClick={() => setShowModal(true)}
						className="text-coral-700 border border-error/30 hover:bg-error/10"
					>
						<Trash2 size={16} />
						{t('profile.deleteAccountButton')}
					</Button>
				</div>
			</div>

			{/* Delete Confirmation Modal */}
			<Modal
				open={showModal}
				onClose={closeModal}
				size="md"
				persistent={isLoading}
				closeLabel={t('common.close')}
				title={
					<span className="flex items-center gap-3">
						<span className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center">
							<AlertTriangle size={20} className="text-error" />
						</span>
						<span>{t('profile.confirmDeleteTitle')}</span>
					</span>
				}
				footer={
					<>
						<Button variant="ghost" onClick={closeModal} disabled={isLoading}>
							{t('common.cancel')}
						</Button>
						<Button
							variant="danger"
							onClick={handleDelete}
							disabled={!confirmed || !preview.data}
							loading={isLoading}
							leadingIcon={!isLoading && <Trash2 size={16} />}
						>
							{isLoading ? t('profile.deleting') : t('profile.deleteAccountButton')}
						</Button>
					</>
				}
			>
				<p className="text-base-content/70 mb-4">{t('profile.confirmDeleteDesc')}</p>
				{error && (
					<Alert tone="error" className="mb-4">
						{error}
					</Alert>
				)}
				{preview.isPending ? (
					<div className="flex justify-center py-4">
						<Spinner size="sm" />
					</div>
				) : preview.isError ? (
					<Alert tone="error">{t('profile.errors.deletePreviewFailed')}</Alert>
				) : (
					<>
						{/* `Space.owner` cascades: every space listed here disappears,
						    with its rooms and services, for every member — not just
						    for the person deleting their account. Say so by name. */}
						{ownedSpaces.length > 0 && (
							<Alert tone="error" className="mb-4">
								<p className="font-medium mb-2">{t('profile.deleteOwnedSpacesWarning')}</p>
								<ul className="list-disc pl-5 space-y-1">
									{ownedSpaces.map((space) => (
										<li key={space.id}>
											<span className="font-medium">{space.name}</span>{' '}
											<span className="opacity-80">
												(
												{t('profile.deleteOwnedSpaceStats', {
													rooms: space.roomCount,
													members: space.memberCount,
												})}
												)
											</span>
										</li>
									))}
								</ul>
							</Alert>
						)}
						{/* `LiveSession.createdBy` cascades: a session this user started
						    ends for every viewer, even one being captioned right now. */}
						{activeLiveSessions.length > 0 && (
							<Alert tone="error" className="mb-4">
								<p className="font-medium mb-2">{t('profile.deleteActiveSessionsWarning')}</p>
								<ul className="list-disc pl-5 space-y-1">
									{activeLiveSessions.map((session) => (
										<li key={session.id}>{session.title}</li>
									))}
								</ul>
							</Alert>
						)}
						{uploadedFileCount > 0 && (
							<p className="text-sm text-base-content/70 mb-4">
								{t('profile.deleteUploadedFilesNote', { count: uploadedFileCount })}
							</p>
						)}
						{hasPassword ? (
							<TextField
								id="delete-password"
								type="password"
								label={t('profile.confirmDeletePassword')}
								placeholder="••••••••"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								leadingIcon={<Lock size={18} />}
								withPasswordToggle
								showPasswordLabel={t('auth.showPassword')}
								hidePasswordLabel={t('auth.hidePassword')}
								disabled={isLoading}
							/>
						) : (
							<TextField
								id="delete-confirm-email"
								// `text`, not `email`: browsers autofill e-mail fields despite
								// `autoComplete="off"`, which would turn the confirmation into
								// a single click.
								type="text"
								autoComplete="off"
								autoCapitalize="off"
								spellCheck={false}
								label={t('profile.confirmDeleteEmail', { email: accountEmail })}
								placeholder={accountEmail}
								value={confirmEmail}
								onChange={(e) => setConfirmEmail(e.target.value)}
								disabled={isLoading}
							/>
						)}
					</>
				)}
			</Modal>
		</>
	)
}
